/**
 * FlagModal — confirmation modal for flagging/reporting inappropriate content.
 * Auth-gated: Cloud Function rejects unauthenticated calls.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { flagMessage } from '../services/pplTalkService';

interface FlagModalProps {
  isOpen: boolean;
  messageId: string;
  onClose: () => void;
  onFlagged?: () => void;
}

const FlagModal: React.FC<FlagModalProps> = ({ isOpen, messageId, onClose, onFlagged }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !submitting) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, submitting, onClose]);

  // Focus trap — cycle Tab within modal
  useEffect(() => {
    if (!isOpen) return;
    const modal = modalRef.current?.querySelector('.flag-modal') as HTMLElement | null;
    if (!modal) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = modal.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === modalRef.current && !submitting) onClose();
    },
    [submitting, onClose]
  );

  const handleFlag = useCallback(async () => {
    setSubmitting(true);
    setError(null);

    try {
      const result = await flagMessage(messageId);
      if (result.success) {
        setSuccess(true);
        onFlagged?.();
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 1500);
      }
    } catch (err: unknown) {
      console.error('Error flagging message:', err);
      const firebaseError = err as { code?: string; message?: string };
      if (firebaseError.code === 'functions/unauthenticated') {
        setError('You must be logged in to report content.');
      } else if (firebaseError.message?.includes('already flagged')) {
        setError('You have already reported this content.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [messageId, onClose, onFlagged]);

  if (!isOpen) return null;

  return (
    <div className="flag-modal-backdrop" ref={modalRef} onClick={handleBackdropClick}>
      <div className="flag-modal" role="dialog" aria-modal="true" aria-label="Report content">
        {success ? (
          <div className="flag-modal-success">
            <span className="flag-modal-success-icon">✓</span>
            <p>Thank you for reporting. We&rsquo;ll review this content.</p>
          </div>
        ) : (
          <>
            <h3 className="flag-modal-title">report this content?</h3>
            <p className="flag-modal-description">
              If this post contains inappropriate, harmful, or spam content, our team will review it.
              Content that receives multiple reports is automatically removed.
            </p>
            {error && <p className="flag-modal-error">{error}</p>}
            <div className="flag-modal-actions">
              <button
                className="btn btn-ghost"
                onClick={onClose}
                disabled={submitting}
                type="button"
              >
                cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleFlag}
                disabled={submitting}
                type="button"
              >
                {submitting ? 'reporting...' : 'report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FlagModal;
